import { useHistory, useLocation, useRouteMatch } from 'react-router';
import {
  Breadcrumbs,
  Copy,
  GoToDetails,
  LabelDate,
  PermissionCheck,
  SearchPanel,
  Table,
} from '../../components';
import { ROUTE_SEGMENT } from '../../app/Routes/const';
import { buildBillingPath } from '../../app/Routes/orgRouteUtils';
import { Filter } from '../billing/components/Filter';
import { BillableTime } from '../billing/components/BillableTime';
import { useConversationsBilling } from './useConversationsBilling';
import { formatDate } from '../knowledge-management/TextCompressorNew/utils/utils';
import { PERMISSIONS } from '../../shared/constants/permissions';
import { useCheckPermissions } from '../../shared';
import { usePermissionAllowed } from '../../hooks';

const ConversationsBillingPage = () => {
  const { params } = useRouteMatch<{ organization_id: string; agent_id: string }>();
  const organizationId = params.organization_id;
  const location = useLocation();
  const history = useHistory();
  const {
    sort,
    setSort,
    setPage,
    filters,
    setFilters,
    intializing,
    pagination,
    conversationsBilling,
    lastRefreshedDate,
    isLastRefreshedDateLoading,
    ...rest
  } = useConversationsBilling(params.agent_id, undefined);

  const { totalText, totalVoice, agentTitle, total, totalVideo } =
    rest?.rawData || {};
  const hasPermissionView = usePermissionAllowed([
    PERMISSIONS.VIEW_CONVERSATIONS,
  ]);

  return (
    <>
      <div className="flex gap-md flex-wrap justify-between items-center mb-md">
        <Breadcrumbs
          className="text-small"
          items={[
            {
              label: 'Billing',
              url: buildBillingPath(organizationId),
            },
            {
              label: agentTitle ?? 'Title',
              url: '',
            },
          ]}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="flex grid-cols-2 gap-4 xl:w-1/2">
          <Filter values={filters ?? {}} onChange={setFilters} />
        </div>
        <div className="flex flex-col justify-center items-center">
          <div className="flex flex-col items-start">
            <div className="flex-shrink-0 mb-6">
              <h1 className="text-3xl sm:text-4xl font-bold text-main">
                {agentTitle}
              </h1>
            </div>
            <div className="flex flex-row flex-wrap items-center gap-4">
              <div className="flex flex-row  gap-4 mr-4">
                <div className="flex flex-col ">
                  <div className="text-medium text-secondary">
                    Total Billable Voice
                  </div>
                  <div className="flex">
                    <BillableTime
                      time={totalVoice}
                      unit="seconds"
                      className="self-end"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-row  gap-4 ">
                <div className="flex flex-col ">
                  <div className="text-medium text-secondary">
                    Total Billable Text
                  </div>
                  <div className="flex">
                    <BillableTime
                      time={totalText}
                      unit="seconds"
                      className="self-end"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-row  gap-4 ">
                <div className="flex flex-col ">
                  <div className="text-medium text-secondary">
                    Total Billable Video
                  </div>
                  <div className="flex">
                    <BillableTime
                      time={totalVideo}
                      unit="seconds"
                      className="self-end"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-row  gap-4 ">
                <div className="flex flex-col">
                  <div className="text-medium text-secondary mb-1">
                    Total Billable Conversations
                  </div>
                  <div className="flex ">
                    <p className="text-4xl sm:text-5xl font-bold text-main leading-none">
                      {total || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {!isLastRefreshedDateLoading && (
              <div className="flex flex-row justify-start sm:justify-end ml-1 mt-8">
                <div className="text-medium flex justify-center">
                  <p className="mr-2">Last updated at:</p>
                  <p className="font-semibold">
                    {formatDate(lastRefreshedDate)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Table
        sort={sort}
        onSort={setSort}
        loading={intializing}
        verticalAlign="middle"
        data={conversationsBilling ?? []}
        emptyMessage="No Billing Information"
        onRowClick={row => {
          hasPermissionView
            ? history.push(
                `${buildBillingPath(organizationId, row.agentId, ROUTE_SEGMENT.CONVERSATION, row.conversationId)}${location.search}`
              )
            : null;
        }}
        config={{
          columns: [
            {
              sortName: 'conversationId',
              header: 'Coversation ID',
              cellClassName:
                'min-w-[12rem] max-w-[24rem] truncate whitespace-nowrap overflow-hidden text-ellipsis align-middle',

              getValue: ({ conversationId }) => (
                <div className="flex items-center">
                  <Copy
                    text={conversationId ?? ''}
                    label=""
                    className="mr-xs shrink-0"
                  />
                  <span className="truncate overflow-hidden text-ellipsis whitespace-nowrap">
                    {conversationId}
                  </span>
                </div>
              ),
            },
            {
              header: 'Started',
              sortName: 'startTime',
              cellClassName: 'min-w-[8rem] max-w-[12rem]',
              getValue: ({ startTime }) =>
                startTime ? (
                  <LabelDate
                    date={startTime}
                    showTime
                    layout="column"
                    formatDate="dd MMM yyyy"
                  />
                ) : (
                  ' - '
                ),
              align: 'center',
            },
            {
              header: 'Finished',
              sortName: 'endTime',
              align: 'center',
              cellClassName: 'min-w-[8rem] max-w-[12rem]',
              getValue: ({ endTime }) =>
                endTime ? (
                  <LabelDate
                    date={endTime}
                    showTime
                    layout="column"
                    formatDate="dd MMM yyyy"
                  />
                ) : (
                  ' - '
                ),
            },
            {
              header: (
                <div>
                  <p>Duration</p>
                  <p>(hh:mm:ss)</p>
                </div>
              ),
              sortName: 'totalSeconds',
              cellClassName: 'min-w-[2rem] w-fit align-middle',
              align: 'center',
              getValue: ({ totalSeconds }) => (
                <BillableTime
                  className="justify-center"
                  numberClassName="text-medium"
                  format="compact"
                  unit="seconds"
                  time={totalSeconds}
                />
              ),
            },
            {
              header: (
                <div>
                  <p>Billable Voice Time</p>
                  <p>(hh:mm:ss)</p>
                </div>
              ),
              sortName: 'voiceSeconds',
              cellClassName: 'min-w-[2rem] w-fit',
              align: 'center',
              getValue: ({ voiceSeconds }) => (
                <BillableTime
                  className="justify-center"
                  numberClassName="text-medium"
                  format="compact"
                  unit="seconds"
                  time={voiceSeconds}
                />
              ),
            },
            {
              header: (
                <div>
                  <p>Billable Text Time</p>
                  <p>(hh:mm:ss)</p>
                </div>
              ),
              sortName: 'textSeconds',
              align: 'center',
              cellClassName: 'min-w-[2rem] w-fit',
              getValue: ({ textSeconds }) => (
                <BillableTime
                  className="justify-center"
                  numberClassName="text-medium"
                  format="compact"
                  unit="seconds"
                  time={textSeconds}
                />
              ),
            },
            {
              header: (
                <div>
                  <p>Billable Video Time</p>
                  <p>(hh:mm:ss)</p>
                </div>
              ),
              sortName: 'videoSeconds',
              align: 'center',
              cellClassName: 'min-w-[2rem] w-fit',
              getValue: ({ videoSeconds }) => (
                <BillableTime
                  className="justify-center"
                  numberClassName="text-medium"
                  format="compact"
                  unit="seconds"
                  time={videoSeconds}
                />
              ),
            },
            {
              header: '',
              cellContentClassName: 'text-right',
              cellClassName: 'max-w-[6rem] min-w-[8rem] align-middle',
              getValue: ({ conversationId, agentId }) => (
                <PermissionCheck action={PERMISSIONS.VIEW_CONVERSATIONS}>
                  <GoToDetails
                    url={buildBillingPath(
                      organizationId,
                      agentId,
                      ROUTE_SEGMENT.CONVERSATION,
                      conversationId
                    )}
                  />
                </PermissionCheck>
              ),
            },
          ],
        }}
      />
      <div className="mt-sm flex justify-between items-center">
        <SearchPanel {...pagination} withPaginate setPage={setPage} />
      </div>
    </>
  );
};

export default ConversationsBillingPage;
